import React from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {
  Phone,
  Email,
  LocalOffer,
  LocationOn,
  Visibility,
  Share as ShareIcon,
  Person,
  Badge,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Store } from "../../types/stores";
import {
  getCategoryConfig,
  formatFloor,
  formatPhoneNumber,
} from "../../utils/storeHelpers";

interface StoreCardProps {
  store: Store;
  index?: number;
  isAdminMode?: boolean; // Admin view shows POC instead of public contact
}

const StoreCard: React.FC<StoreCardProps> = ({
  store,
  index = 0,
  isAdminMode = false,
}) => {
  const navigate = useNavigate();
  const categoryConfig = getCategoryConfig(store.category);
  const Icon = categoryConfig.icon;

  const handleCardClick = () => {
    // Admin mode doesn't navigate, only public mode does
    if (!isAdminMode) {
      navigate(`/stores/${store.id}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      style={{ height: "100%" }}
    >
      <Card
        elevation={2}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: isAdminMode ? "default" : "pointer",
          borderRadius: { xs: 2.5, sm: 3, md: 3.5 },
          boxShadow: "0 2px 12px rgba(26, 35, 126, 0.12)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          border: "1px solid",
          borderColor: "divider",
          "&:hover": isAdminMode
            ? {}
            : {
                transform: "translateY(-6px)",
                boxShadow: "0 12px 32px rgba(26, 35, 126, 0.18)",
                borderColor: "primary.light",
                "& .store-image": {
                  transform: "scale(1.05)",
                },
              },
        }}
        onClick={handleCardClick}
      >
        {/* Store Image - Compact height */}
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <CardMedia
            component="img"
            height="160"
            image={store.imageUrl}
            alt={store.name}
            className="store-image"
            sx={{
              objectFit: "cover",
              backgroundColor: "grey.100",
              transition: "transform 0.3s ease",
            }}
          />

          {/* Category Badge - Compact */}
          <Box
            sx={{
              position: "absolute",
              top: { xs: 8, md: 12 },
              left: { xs: 8, md: 12 },
            }}
          >
            <Chip
              icon={<Icon sx={{ fontSize: { xs: 14, md: 16 } }} />}
              label={categoryConfig.label}
              size="small"
              sx={{
                background: categoryConfig.gradient,
                color: "white",
                fontWeight: 600,
                fontSize: { xs: "0.688rem", md: "0.75rem" },
                height: { xs: 24, md: 28 },
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            />
          </Box>

          {/* Active Offers Badge - Compact */}
          {store.activeOffers !== undefined && store.activeOffers > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: { xs: 8, md: 12 },
                right: { xs: 8, md: 12 },
              }}
            >
              <Chip
                icon={<LocalOffer sx={{ fontSize: { xs: 14, md: 16 } }} />}
                label={`${store.activeOffers} ${
                  store.activeOffers > 1 ? "Offers" : "Offer"
                }`}
                size="small"
                sx={{
                  background:
                    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.688rem", md: "0.75rem" },
                  height: { xs: 24, md: 28 },
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            p: { xs: 1.5, md: 2 },
            pt: { xs: 1, md: 1.25 },
          }}
        >
          {/* Store Name - Compact */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                fontSize: { xs: "0.938rem", md: "1.063rem" },
                lineHeight: 1.3,
                minHeight: { xs: "2.4rem", md: "2.7rem" },
              }}
            >
              {store.name}
            </Typography>
            <Visibility
              sx={{
                fontSize: { xs: 16, md: 18 },
                color: "primary.main",
                opacity: 0.7,
                ml: 0.75,
                flexShrink: 0,
              }}
            />
          </Box>

          {/* Location Info - Compact */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: store.viewCount ? 1.5 : 1,
            }}
          >
            <LocationOn
              sx={{ fontSize: { xs: 16, md: 18 }, color: "primary.main" }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: "0.813rem", md: "0.875rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatFloor(store.floor)} • {store.shopNumber}
              {store.zone && ` • ${store.zone}`}
            </Typography>
          </Box>

          {/* Exclusive Badge (left) and View/Share Counts (right) - Same Line */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mt: "auto",
            }}
          >
            {/* Exclusive Badge - Left */}
            {store.tags?.includes("exclusive") && (
              <Chip
                label="EXCLUSIVE"
                size="small"
                icon={<span style={{ color: "#8B4513" }}>⭐</span>}
                sx={{
                  backgroundColor: "rgba(255,215,0,0.95)",
                  color: "#000",
                  fontWeight: 700,
                  fontSize: { xs: "0.625rem", md: "0.688rem" },
                  height: { xs: 22, md: 24 },
                  boxShadow: 2,
                  "& .MuiChip-icon": {
                    fontSize: { xs: 12, md: 14 },
                    marginLeft: "4px",
                  },
                }}
              />
            )}

            {/* View and Share Counts - Right */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                ml: "auto",
              }}
            >
              {/* View Count */}
              {store.viewCount !== undefined && store.viewCount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Visibility
                    sx={{
                      fontSize: { xs: 14, md: 16 },
                      color: "text.secondary",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", md: "0.813rem" },
                      fontWeight: 500,
                    }}
                  >
                    {store.viewCount.toLocaleString()} view
                    {store.viewCount !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              )}

              {/* Share Count */}
              {store.shareCount !== undefined && store.shareCount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <ShareIcon
                    sx={{
                      fontSize: { xs: 14, md: 16 },
                      color: "text.secondary",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", md: "0.813rem" },
                      fontWeight: 500,
                    }}
                  >
                    {store.shareCount.toLocaleString()} share
                    {store.shareCount !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Contact Info - Vertical Layout */}
          {isAdminMode && store.pointOfContact ? (
            // ADMIN MODE: Show Point of Contact Details
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: "auto",
                pt: 1.5,
                borderTop: "2px solid",
                borderColor: "primary.light",
                bgcolor: "primary.50",
                p: 1.5,
                borderRadius: 1.5,
              }}
            >
              {/* POC Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 0.5,
                }}
              >
                <Person
                  sx={{
                    fontSize: { xs: 18, md: 20 },
                    color: "primary.main",
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    fontSize: { xs: "0.813rem", md: "0.875rem" },
                  }}
                >
                  Point of Contact
                </Typography>
              </Box>

              {/* POC Name */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Badge
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    color: "text.secondary",
                  }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.813rem", md: "0.875rem" },
                    }}
                  >
                    {store.pointOfContact.name}
                  </Typography>
                  {store.pointOfContact.designation && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: { xs: "0.688rem", md: "0.75rem" },
                      }}
                    >
                      {store.pointOfContact.designation}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* POC Phone */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: "success.50",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "success.100",
                    transform: "translateX(4px)",
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = `tel:${store.pointOfContact?.mobile}`;
                }}
              >
                <Phone
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    color: "success.main",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.813rem" },
                    fontWeight: 600,
                    color: "success.main",
                  }}
                >
                  {formatPhoneNumber(store.pointOfContact.mobile)}
                </Typography>
              </Box>

              {/* POC Email */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: "info.50",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "info.100",
                    transform: "translateX(4px)",
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = `mailto:${store.pointOfContact?.email}`;
                }}
              >
                <Email
                  sx={{
                    fontSize: { xs: 16, md: 18 },
                    color: "info.main",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.813rem" },
                    fontWeight: 600,
                    color: "info.main",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {store.pointOfContact.email}
                </Typography>
              </Box>

              {/* Alternative Mobile */}
              {store.pointOfContact.alternativeMobile && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: "warning.50",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "warning.100",
                      transform: "translateX(4px)",
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `tel:${store.pointOfContact?.alternativeMobile}`;
                  }}
                >
                  <Phone
                    sx={{
                      fontSize: { xs: 16, md: 18 },
                      color: "warning.main",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { xs: "0.625rem", md: "0.688rem" },
                        color: "text.secondary",
                        display: "block",
                      }}
                    >
                      Alternative
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.75rem", md: "0.813rem" },
                        fontWeight: 600,
                        color: "warning.main",
                      }}
                    >
                      {formatPhoneNumber(
                        store.pointOfContact.alternativeMobile,
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            // PUBLIC MODE: Show regular contact info
            (store.contactNumber || store.email) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  mt: "auto",
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                {/* Phone Number */}
                {store.contactNumber && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      p: { xs: 0.75, md: 1 },
                      borderRadius: 1.5,
                      bgcolor: "primary.50",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "primary.100",
                      },
                    }}
                  >
                    <Phone
                      sx={{
                        fontSize: { xs: 16, md: 18 },
                        color: "primary.main",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.75rem", md: "0.813rem" },
                        fontWeight: 600,
                        color: "primary.main",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatPhoneNumber(store.contactNumber)}
                    </Typography>
                  </Box>
                )}

                {/* Email */}
                {store.email && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      p: { xs: 0.75, md: 1 },
                      borderRadius: 1.5,
                      bgcolor: "secondary.50",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "secondary.100",
                      },
                    }}
                  >
                    <Email
                      sx={{
                        fontSize: { xs: 16, md: 18 },
                        color: "secondary.main",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.75rem", md: "0.813rem" },
                        fontWeight: 600,
                        color: "secondary.main",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {store.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            )
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default StoreCard;
